/**
 * Conditions / Problems helpers shared between the chart-tab and the
 * inline editor.
 *
 * These were originally exported from `add-problem-editor.tsx` but
 * exporting helper utilities from a component file is fragile —
 * future consumers might assume the export tree is component-only,
 * and tree-shaking can't always split them cleanly. Samer PR #225
 * review §3 — moved here so the helpers live alongside the type
 * contracts they enforce.
 */

import {
  EMPTY_BODY_SITE,
  type ConditionBodySite,
  type ConditionCode,
  type PatientCondition,
} from '@/types/patient-chart-api';

/**
 * Lock condition codes to the strict 6-field shape (Definition included).
 * The master endpoint ships some fields as `undefined` rather than
 * `null`; JSON.stringify would drop them so we coalesce to explicit
 * null. Flutter parity is the 6-field shape with present-with-null
 * for missing values.
 */
export function normalizeConditionCode(
  c: ConditionCode | null | undefined,
): ConditionCode | null {
  if (!c) return null;
  return {
    code: c.code ?? null,
    Definition: c.Definition ?? null,
    display: c.display ?? null,
    system: c.system ?? null,
    description: c.description ?? null,
    shortName: c.shortName ?? null,
  };
}

/**
 * `bodySite` on UPDATE — coalesces missing fields against the frozen
 * `EMPTY_BODY_SITE` sentinel so the wire body always carries the
 * three-key shape the backend expects.
 *
 * Returns a FRESH object each call (never the sentinel itself) so
 * callers can safely spread or mutate the returned value without
 * affecting other consumers. Samer PR #225 review §3.
 */
export function emptyBodySite(
  bs: PatientCondition['bodySite'] | null | undefined,
): ConditionBodySite {
  if (!bs) return { ...EMPTY_BODY_SITE };
  return {
    system: bs.system ?? null,
    code: bs.code ?? null,
    display: bs.display ?? null,
  };
}

/**
 * Build both `endDate` (ISO no-trailing-Z) and `endDisplayDate`
 * (DD/MM/YYYY) from a single source date. Two fields, one source —
 * they can't drift because they're computed from the same parsed
 * Date object.
 *
 * Input: `YYYY-MM-DD` string from `<input type="date">`.
 * Returns `null` when input is empty/unparseable so callers can
 * block submit with a clear error rather than ship malformed data.
 */
export function buildResolveDateFields(
  yyyymmdd: string | null | undefined,
): { endDate: string; endDisplayDate: string } | null {
  if (!yyyymmdd) return null;
  const parts = yyyymmdd.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts;
  if (!y || !m || !d) return null;
  // Validate by round-tripping through Date so "2026-13-45" is rejected.
  const parsed = new Date(`${yyyymmdd}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    endDate: `${yyyymmdd}T00:00:00.000`,
    endDisplayDate: `${d}/${m}/${y}`,
  };
}

/**
 * Client-set `createdDate` for the CreateCondition wire body. ISO
 * `YYYY-MM-DDTHH:mm:ss.sss` without the trailing `Z` (the format
 * the .NET model binder accepts).
 *
 * ⚠️ **PHI integrity caveat** (Samer PR #225 review §1): the value
 * depends on the client's device clock + timezone. Curl observation
 * suggests the backend overwrites this on persist (UPDATE response
 * preserved server time, not the curl payload's client-set time) —
 * so device clock skew is harmless under that assumption. The field
 * can't be omitted (backend rejects missing key with 400).
 *
 * **Tracked backend alignment**: PROD-837
 * (https://scrips.atlassian.net/browse/PROD-837). If a future dev
 * observes drift between the shipped value and the stored value
 * (the canary), flag on that ticket — the assumption above has
 * broken.
 *
 * PROD-1778: this used to be `new Date().toISOString().replace(/Z$/, '')`.
 * That is a FALSE safeguard — `toISOString()` has already converted the
 * instant to UTC, so dropping the `Z` does not restore local time, it BAKES
 * THE OFFSET IN. The backend binds the literal, so 13:00 Asia/Dubai was
 * stored as 09:00. Now delegates to `localNowIso()` below — the correct
 * sibling that reads the LOCAL clock — so the two helpers agree.
 */
export function clientCreatedDate(): string {
  return localNowIso();
}

/**
 * Local wall-clock "now" as `YYYY-MM-DDTHH:mm:ss.sss` (no trailing Z).
 *
 * `clientCreatedDate()` above delegates here (PROD-1778); it previously used
 * `toISOString()` (UTC) and drifted 4h from this function in UAE. For a
 * **clinical** date that may be persisted as-sent (e.g. F-PAT-273 unresolve
 * resets `onsetDateTime` to now), we must match Flutter's `DateTime.now()`,
 * which is the device's LOCAL time. Using UTC here would write an onset 4h off
 * from Flutter for the same action in UAE (UTC+4) — a PHI/audit divergence
 * (Samer review #491 §1). Mirrors the local-date convention already used for
 * picked dates (`buildResolveDateFields` ships the picked local date at
 * midnight, not a UTC-shifted one).
 */
export function localNowIso(): string {
  const d = new Date();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `.${pad(d.getMilliseconds(), 3)}`
  );
}

/**
 * F-PAT-272: true when a problem's onset is strictly AFTER the chosen
 * resolution (end) date. Flutter `problems_tab.dart:1064-1080` blocks the
 * Resolve action with a toast in exactly this case
 * (`onsetDateTime.isAfter(endDate)`).
 *
 * `onset` is an ISO-ish datetime string; `endYyyymmdd` is the `YYYY-MM-DD`
 * from `<input type="date">`. Returns false when either is blank/unparseable
 * — don't block on missing data (other guards handle empties).
 *
 * Timezone (Samer review #491 §4): both sides are parsed WITHOUT a `Z`, so
 * the JS `Date` constructor reads them in the **same** (local) zone — there's
 * no UTC-vs-local skew between `onset` and `end`. The stored onset follows the
 * project's no-Z convention; if a value ever arrives WITH a `Z` (UTC) it would
 * compare against the local end, but that's not a shape the backend emits here.
 */
export function isOnsetAfterEnd(
  onset: string | null | undefined,
  endYyyymmdd: string | null | undefined,
): boolean {
  if (!onset || !endYyyymmdd) return false;
  const o = new Date(onset);
  const e = new Date(`${endYyyymmdd}T00:00:00`);
  if (Number.isNaN(o.getTime()) || Number.isNaN(e.getTime())) return false;
  return o > e;
}
